<template>
  <v-card
    flat
    outlined
    rounded="lg"
    height="100%"
    color="cardbg"
    class="flex"
    elevation="10"
  >
    <v-card-title
      color="primary"
      :style="`
        padding: calc(${scale} * 0.4rem);
        line-height: calc(${scale} * 0.75rem);
        font-size: calc(${scale} * 0.7rem);
      `"
      class="font-weight-medium titlecolor--text d-flex justify-space-between"
    >
      <span :style="`font-size: calc(${scale} * 0.7rem);`">
        {{ title }}
      </span>
      <span class="subtitlecolor--text">{{ daterange }}</span>
    </v-card-title>
    <v-card-text style="height: 90%; padding: 0; line-height: normal;">
      <WidgetLoading v-if="loading" />
      <div
        v-else-if="noData"
        class="subtitlecolor--text d-flex align-center justify-center"
        style="height: 100%; width: 100%;"
        :style="`font-size: calc(${scale} * 0.75rem);`"
      >
        No Data Available
      </div>
      <div style="height: 100%; width: 100%;" class="d-flex" v-else>
        <div style="height: 100%; width: 60%; padding-right: 0%;">
          <div
            class="d-flex align-content-start"
            :style="`padding: calc(${scale} * 0.4rem);`"
            style="flex-wrap: wrap; width: 100%; height: 100%; overflow: hidden;"
          >
            <div
              v-for="legend in legends" :key="legend.name"
              class="d-flex"
              :style="`
                width: 33.33%;
                height: ${legendItemHeight}%;
                flex-direction: column;
                overflow: hidden;
                padding-right: 4px;
              `"
            >
              <div
                :class="['font-weight-medium', legendValueTextClass]"
                :style="`
                  font-size: calc(${scale} * ${legendValueSize}rem);
                  line-height: 1.05;
                  margin-bottom: 1px;
                `"
              >
                {{ legend.y }}%
              </div>
              <div
                class="subtitlecolor--text d-flex align-center"
                :style="`font-size: calc(${scale} * ${legendNameSize}rem); width: 100%;`"
              >
                <span
                  :style="`
                    background: ${legend.color};
                    color: ${legend.color};
                    font-size: calc(${scale} * 0.6rem);
                    margin-right: 8%;
                    flex-shrink: 0;
                  `"
                >00</span>
                <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
                 display: block;">
                  {{ legend.name }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div style="width: 40%; height: 100%;">
          <highcharts
            :options="options"
            style="height: 111%; width: 111%; margin-left: -12%; margin-top: -14%;"
            :key="uuid"
          ></highcharts>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from 'vuex';
import WidgetLoading from './WidgetLoading.vue';

export default {
  name: 'PieChart',
  props: {
    title: { type: String },
    daterange: { type: String },
    scale: { type: Number, default: 1 },
    unit: { type: String },
    loading: { type: Boolean, default: false },
  },
  components: { WidgetLoading },
  data() {
    return {
      uuid: 'uuid',
      legends: [],
      options: {
        chart: {
          backgroundColor: 'transparent',
          style: {
            fontFamily: 'Poppins, "Open Sans", sans-serif',
          },
          plotBackgroundColor: null,
          plotBorderWidth: null,
          plotShadow: false,
          type: 'pie',
        },
        legend: { enabled: false },
        title: {
          text: '',
          align: 'center',
          verticalAlign: 'middle',
          x: -50,
          y: -10,
          style: { color: '#FFFFFF', fontSize: 11, lineHeight: 10 },
        },
        tooltip: { pointFormat: '{series.name}:<b>{point.percentage:.1f}%</b>' },
        accessibility: { point: { valueSuffix: '' } },
        plotOptions: {
          series: { borderWidth: 0 },
          pie: {
            innerSize: '45%',
            size: '82%',
            showInLegend: true,
            cursor: 'pointer',
            dataLabels: { enabled: false },
          },
        },
        series: [],
      },
    };
  },
  watch: {
    scale(val) {
      this.adjustSize(val);
      this.createUUID();
    },
    activeTheme() {
      this.adjustSize(this.scale);
      this.createUUID();
    },
  },
  computed: {
    ...mapState('cellwiseDowntimeDashboard', ['theme']),
    activeTheme() {
      return (this.theme && this.theme.isDark) ? 'dark' : 'light';
    },
    legendItemHeight() {
      return 100 / Math.max(Math.ceil(this.legends.length / 3), 1);
    },
    legendValueSize() {
      if (this.legends.length > 12) return 0.78;
      if (this.legends.length > 9) return 0.92;
      return 1.2;
    },
    legendNameSize() {
      if (this.legends.length > 12) return 0.56;
      if (this.legends.length > 9) return 0.65;
      return 0.8;
    },
    legendValueTextClass() {
      return (this.theme && this.theme.isDark)
        ? 'titlecolor--text'
        : 'subtitlecolor--text';
    },
    noData() {
      return !this.legends || !this.legends.some((legend) => Number(legend.y) > 0);
    },
  },
  methods: {
    assignData(result) {
      if (result && result.series && result.series.length > 0) {
        this.options.series = result.series;
        this.legends = result.series[0].data || [];
      } else {
        this.options.series = [{ name: 'Downtime', data: [] }];
        this.legends = [];
      }
      this.adjustSize(this.scale);
      this.createUUID();
    },
    adjustSize(scale) {
      const lablesize = 9 * scale;
      const distance = 18 * scale;
      const lineheight = 12 * scale;

      const isDark = this.activeTheme === 'dark';
      const themes = this.theme && this.theme.themes;
      const currentTheme = themes && themes[this.activeTheme]
        ? themes[this.activeTheme]
        : { subtitlecolor: '#A1A3A5', titlecolor: '#15191E' };

      let textColor = currentTheme.titlecolor;
      if (isDark) {
        textColor = '#FFFFFF';
      } else if (textColor === '#FFFFFF') {
        textColor = '#15191E';
      }

      this.options.plotOptions.pie.dataLabels = {
        enabled: false,
        distance: -distance,
        format: '{point.percentage:.0f}',
        style: {
          color: '#FFFFFF',
          fontSize: `${lablesize}px`,
          fontWeight: 'normal',
          textOutline: 'none',
          fontFamily: 'Poppins, "Open Sans", sans-serif',
        },
      };

      this.options.title.style.fontSize = `${lablesize}px`;
      this.options.title.style.lineHeight = `${lineheight}px`;
      this.options.title.style.color = textColor;
      this.options.title.style.fontWeight = 'normal';
      this.options.title.style.fontFamily = 'Poppins, "Open Sans", sans-serif';
    },
    createUUID() {
      let dt = new Date().getTime();
      const newUuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.floor((dt + Math.random() * 16) % 16);
        dt = Math.floor(dt / 16);
        const value = c === 'x' ? r : (r % 4) + 8;
        return value.toString(16);
      });
      this.uuid = newUuid;
    },
  },
};
</script>
